import { FC, useEffect, useState } from 'react';

import styled from 'styled-components';
import moment from 'moment';
import dynamic from 'next/dynamic';

import { useBreakpoint } from '@contexts/breakpoints';
import { DateType, formatMomentDate } from '@utils/dateUtil';
import useFlightSearchBar from '@domain/flight/context/flight';
import { FlightSearchBarActionType } from '@domain/flight/context/flight/actions';
import { getDateRangePickerPlaceholder } from '@components/views/flight/listing/SearchBar/DateSelect/utils';

const SearchBarSingleDatePicker = dynamic(
  () =>
    import(
      '@components/views/flight/listing/SearchBar/DateSelect/DateSelect'
    ).then((mod: any) => mod['SearchBarSingleDatePicker']) as Promise<
      React.FC<any>
    >
);

const SearchBarRangeDatePicker = dynamic(
  () =>
    import(
      '@components/views/flight/listing/SearchBar/DateSelect/DateSelect'
    ).then((mod: any) => mod['SearchBarRangeDatePicker']) as Promise<
      React.FC<any>
    >
);

const ResponsiveSingleDateSelect = dynamic(
  () =>
    import(
      '@components/views/flight/listing/SearchBar/DateSelect/ResponsiveSingleDateSelect'
    )
);

const ResponsiveDateRangeSelectDrawer = dynamic(
  () =>
    import(
      '@components/views/flight/listing/SearchBar/DateSelect/ResponsiveDateRangeSelect'
    )
);

const SearchBarDateInputWrapper = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: ${({ theme }) => theme.palette.white};
  border-bottom: 1px solid #000;

  @media (min-width: ${({ theme }) => theme.breakPoint.TABLET}) {
    border-bottom: 0px solid #000;
  }
`;

interface Dates {
  startDate?: DateType | null;
  endDate?: DateType | null;
}

const FlightDatePicker: FC<{
  initialDates?: Dates;
}> = ({ initialDates }) => {
  const { state, dispatch } = useFlightSearchBar();
  const { xl } = useBreakpoint();

  const [localStartDate, setLocalStartDate] = useState<DateType | null>(
    initialDates?.startDate || null
  );
  const [localEndDate, setLocalEndDate] = useState<DateType | null>(
    initialDates?.endDate || null
  );

  useEffect(() => {
    if (!state.isOneWay) {
      const diff = moment(localEndDate?.toDate()).diff(
        localStartDate?.toDate(),
        'days'
      );

      if (diff < 0) {
        dispatch({
          type: FlightSearchBarActionType.SET_END_DATE,
          payload: null,
        });
        setLocalEndDate(null);
      }
    }
  }, [state.isOneWay]);

  useEffect(
    () => setLocalStartDate(state.startDate ? moment(state.startDate) : null),
    [state.startDate]
  );

  useEffect(
    () => setLocalEndDate(state.endDate ? moment(state.endDate) : null),
    [state.endDate]
  );

  const onDatesChange = ({
    startDate,
    endDate,
  }: {
    startDate: DateType | null;
    endDate: DateType | null;
  }) => {
    dispatch({
      type: FlightSearchBarActionType.SET_START_DATE,
      payload: startDate?.toDate(),
    });
    dispatch({
      type: FlightSearchBarActionType.SET_END_DATE,
      payload: endDate?.toDate(),
    });

    setLocalStartDate(startDate);
    setLocalEndDate(endDate);
  };

  const onDateChange = (date: moment.Moment | null) => {
    setLocalStartDate(date);
    dispatch({
      type: FlightSearchBarActionType.SET_START_DATE,
      payload: date?.toDate(),
    });
  };

  return (
    <SearchBarDateInputWrapper>
      {state.isOneWay ? (
        xl || xl === undefined ? (
          <SearchBarSingleDatePicker
            date={localStartDate}
            onDateChange={onDateChange}
            inputComponent='shopAndFlyHomePageFlight'
          />
        ) : (
          <ResponsiveSingleDateSelect
            date={localStartDate}
            onDateChange={onDateChange}
            title='Gidiş Tarihiniz?'
            subTitle={`${formatMomentDate(localStartDate)}`}
            placeholder='Gidiş Tarihi'
            drawerTitle='Tarih Arama'
            arrowIcon={true}
            inputComponent='shopAndFlyHomePageFlight'
          />
        )
      ) : null}
      {!state.isOneWay ? (
        xl || xl === undefined ? (
          <SearchBarRangeDatePicker
            minimumNights={0}
            startDate={localStartDate}
            endDate={localEndDate}
            onDatesChange={onDatesChange}
            inputComponent='shopAndFlyHomePageFlight'
          />
        ) : (
          <ResponsiveDateRangeSelectDrawer
            startDate={localStartDate}
            endDate={localEndDate}
            onDatesChange={onDatesChange}
            title='Gidiş-Dönüş Tarihiniz?'
            subTitle={getDateRangePickerPlaceholder(
              localStartDate,
              localEndDate
            )}
            placeholder='Gidiş - Dönüş Tarihleri'
            drawerTitle='Tarih Arama'
            arrowIcon={true}
            inputComponent='shopAndFlyHomePageFlight'
          />
        )
      ) : null}
    </SearchBarDateInputWrapper>
  );
};
export default FlightDatePicker;
